import {
  createInvoiceRequestSchema,
  invoiceTemplateSchema,
  type InvoiceTemplate,
  type InvoiceDraftResponse,
  type CreateInvoiceRequest,
  type Customer,
  type InvoiceDetail,
  type SellerProfile,
} from "@stb/api-contract"
import {
  computeInvoiceTotals,
  validateInvoice,
  type InvoiceIssue,
  type InvoiceTotals,
} from "@stb/jurisdiction-au/invoice"

import {
  buildInvoiceDocument,
  SELLER_NAME_PLACEHOLDER,
  type InvoiceDocumentData,
  type InvoiceDocumentInput,
} from "./document"
import {
  formatAmountInput,
  formatQuantityInput,
  parseAmountMinor,
  parseQuantityMilli,
} from "./format"

/**
 * The invoice editor's form state and the pure functions around it (no React, no server code):
 * text fields in, a strict `CreateInvoiceRequest` out, plus the live totals, preview data and the
 * same Send blockers the server applies (`validateInvoice`), so the form can show them inline.
 */

export interface LineForm {
  /** Stable React key; never sent. */
  key: string
  description: string
  quantity: string
  unitAmount: string
  taxCode: "GST" | "GST_FREE"
}

export interface InvoiceForm {
  customerId: string | null
  buyerName: string
  buyerAbn: string
  issueDate: string
  dueDate: string
  amountsAre: "inclusive" | "exclusive"
  gstRounding: "per-line" | "per-document"
  lines: LineForm[]
  note: string
  showBankDetails: boolean
  /** Template picked on this draft; null follows the organisation default. */
  template: InvoiceTemplate | null
}

export type FieldErrors = Record<string, string>

const DAY_MS = 86_400_000

/** `2026-10-27` + 14 -> `2026-11-10` (calendar days, no time zone involved). */
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/**
 * Lines present at first render get index keys (`line-0`, ...): the server and the browser must
 * render the same ids or React reports a hydration mismatch. Lines added later by a click use a
 * counter, which only ever runs in the browser.
 */
let addedCounter = 0
export function newLine(overrides: Partial<LineForm> = {}, initialIndex?: number): LineForm {
  const key = initialIndex === undefined ? `line-added-${++addedCounter}` : `line-${initialIndex}`
  return {
    key,
    description: "",
    quantity: "1",
    unitAmount: "",
    taxCode: "GST",
    ...overrides,
  }
}

export function emptyForm(options: { today: string; termsDays: number }): InvoiceForm {
  return {
    customerId: null,
    buyerName: "",
    buyerAbn: "",
    issueDate: options.today,
    dueDate: addDays(options.today, options.termsDays),
    amountsAre: "exclusive",
    gstRounding: "per-document",
    lines: [newLine({}, 0)],
    note: "",
    showBankDetails: true,
    template: null,
  }
}

export function formFromInvoice(invoice: InvoiceDetail): InvoiceForm {
  return {
    customerId: invoice.customer_id,
    buyerName: invoice.customer_id ? "" : (invoice.buyer_name ?? ""),
    buyerAbn: invoice.customer_id ? "" : (invoice.buyer_abn ?? ""),
    issueDate: invoice.issue_date,
    dueDate: invoice.due_date,
    amountsAre: invoice.amounts_are,
    gstRounding: invoice.gst_rounding,
    lines:
      invoice.lines.length > 0
        ? invoice.lines.map((line, index) =>
            newLine(
              {
                description: line.description,
                quantity: formatQuantityInput(line.quantity_milli),
                unitAmount: formatAmountInput(line.unit_amount_minor),
                taxCode: line.tax_code,
              },
              index,
            ),
          )
        : [newLine({}, 0)],
    note: invoice.note ?? "",
    showBankDetails: invoice.show_bank_details,
    // a legacy stored value the contract no longer lists falls back to the default
    template: invoiceTemplateSchema.safeParse(invoice.template).data ?? null,
  }
}

/** Chooses (or clears) the customer: their terms set the due date, an ad hoc buyer is dropped. */
export function applyCustomer(
  form: InvoiceForm,
  customer: Customer | null,
  defaultTermsDays: number,
): InvoiceForm {
  if (!customer) return { ...form, customerId: null }
  return {
    ...form,
    customerId: customer.id,
    buyerName: "",
    buyerAbn: "",
    dueDate: addDays(form.issueDate, customer.payment_terms_days ?? defaultTermsDays),
  }
}

const orNull = (text: string) => (text.trim() === "" ? null : text.trim())

/** Strict request from the text fields, or the field errors keyed like `lines.0.unit_amount`. */
export function buildRequest(
  form: InvoiceForm,
): { ok: true; request: CreateInvoiceRequest } | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {}
  const lines = form.lines.map((line, index) => {
    const quantity = parseQuantityMilli(line.quantity)
    const unit = parseAmountMinor(line.unitAmount)
    if (!line.description.trim()) errors[`lines.${index}.description`] = "Describe this line."
    if (quantity === null) errors[`lines.${index}.quantity`] = "Enter a quantity above 0."
    if (unit === null) errors[`lines.${index}.unit_amount`] = "Enter an amount like 120.00."
    return {
      description: line.description.trim(),
      quantity_milli: quantity ?? 0,
      unit_amount_minor: unit ?? 0,
      tax_code: line.taxCode,
    }
  })
  if (Object.keys(errors).length > 0) {
    // Still check the rest of the form so every problem shows at once.
    collectSchemaErrors(form, lines, errors)
    return { ok: false, errors }
  }
  const request: CreateInvoiceRequest = {
    customer_id: form.customerId,
    buyer_name: form.customerId ? null : orNull(form.buyerName),
    buyer_abn: form.customerId ? null : orNull(form.buyerAbn),
    issue_date: form.issueDate,
    due_date: form.dueDate,
    amounts_are: form.amountsAre,
    gst_rounding: form.gstRounding,
    lines,
    note: orNull(form.note),
    show_bank_details: form.showBankDetails,
    template: form.template,
  }
  const parsed = createInvoiceRequestSchema.safeParse(request)
  if (!parsed.success) {
    collectSchemaErrors(form, lines, errors)
    return { ok: false, errors: { ...errors, ...schemaErrors(parsed.error.issues) } }
  }
  return { ok: true, request }
}

const FIELD_MESSAGES: Record<string, string> = {
  due_date: "The due date cannot be before the issue date.",
  issue_date: "Enter an issue date.",
  buyer_abn: "Enter a valid 11 digit ABN.",
  buyer_name: "The buyer name is too long.",
  note: "The note is too long.",
}

function schemaErrors(issues: readonly { path: PropertyKey[]; message: string }[]): FieldErrors {
  const errors: FieldErrors = {}
  for (const issue of issues) {
    const [head, index, leaf] = issue.path
    const key =
      head === "lines" && typeof index === "number" && typeof leaf === "string"
        ? `lines.${index}.${leaf === "unit_amount_minor" ? "unit_amount" : leaf === "quantity_milli" ? "quantity" : leaf}`
        : String(head)
    errors[key] ??= FIELD_MESSAGES[key] ?? issue.message
  }
  return errors
}

function collectSchemaErrors(
  form: InvoiceForm,
  lines: CreateInvoiceRequest["lines"],
  errors: FieldErrors,
): void {
  // Non-line fields only: line problems were already reported with friendlier messages.
  const probe = createInvoiceRequestSchema.safeParse({
    customer_id: form.customerId,
    buyer_name: form.customerId ? null : orNull(form.buyerName),
    buyer_abn: form.customerId ? null : orNull(form.buyerAbn),
    issue_date: form.issueDate,
    due_date: form.dueDate,
    amounts_are: form.amountsAre,
    gst_rounding: form.gstRounding,
    lines: lines.map((line) => ({
      ...line,
      quantity_milli: 1,
      unit_amount_minor: 0,
      description: "x",
    })),
    note: orNull(form.note),
    show_bank_details: form.showBankDetails,
  })
  if (!probe.success) {
    for (const [key, message] of Object.entries(schemaErrors(probe.error.issues))) {
      if (!key.startsWith("lines")) errors[key] ??= message
    }
  }
}

/** Live totals from the text fields; unreadable lines count as 0 until fixed. */
export function computeFormTotals(form: InvoiceForm, gstRegistered: boolean): InvoiceTotals {
  return computeInvoiceTotals(
    form.lines.map((line) => ({
      quantityMilli: parseQuantityMilli(line.quantity) ?? 0,
      unitAmountMinor: parseAmountMinor(line.unitAmount) ?? 0,
      taxCode: line.taxCode,
    })),
    { amountsAre: form.amountsAre, rounding: form.gstRounding, gstRegistered },
  )
}

function addressLines(seller: SellerProfile): string[] {
  const place = [seller.suburb, seller.state, seller.postcode].filter(Boolean).join(" ")
  return [seller.address_line1, seller.address_line2, place].filter((part): part is string =>
    Boolean(part),
  )
}

/** The same `InvoiceDocumentData` the PDF and the email are built from, for the live preview. */
export function previewDocument(
  form: InvoiceForm,
  seller: SellerProfile,
  customer: Customer | null,
  number: string | null = null,
): InvoiceDocumentData {
  const totals = computeFormTotals(form, seller.gst_registered)
  const input: InvoiceDocumentInput = {
    seller: {
      businessName: seller.business_name?.trim() || SELLER_NAME_PLACEHOLDER,
      abn: seller.abn,
      gstRegistered: seller.gst_registered,
      addressLines: addressLines(seller),
      bank: {
        accountName: seller.bank_account_name,
        bsb: seller.bank_bsb,
        accountNumber: seller.bank_account_number,
      },
    },
    buyer: {
      name: customer?.name ?? orNull(form.buyerName),
      abn: customer?.abn ?? orNull(form.buyerAbn),
      address: customer?.address ?? null,
    },
    number,
    documentTitle: seller.gst_registered ? "tax_invoice" : "invoice",
    issueDate: form.issueDate,
    dueDate: form.dueDate,
    amountsAre: form.amountsAre,
    note: orNull(form.note),
    showBankDetails: form.showBankDetails,
    lines: form.lines.map((line, index) => ({
      description: line.description.trim() || "Description",
      quantityMilli: parseQuantityMilli(line.quantity) ?? 0,
      unitAmountMinor: parseAmountMinor(line.unitAmount) ?? 0,
      taxCode: line.taxCode,
      netMinor: totals.lines[index]!.netMinor,
      gstMinor: totals.lines[index]!.gstMinor,
      grossMinor: totals.lines[index]!.grossMinor,
    })),
    subtotalMinor: totals.subtotalMinor,
    gstMinor: totals.gstMinor,
    totalMinor: totals.totalMinor,
    roundingAdjustmentMinor: totals.roundingAdjustmentMinor,
  }
  return buildInvoiceDocument(input)
}

/** What would stop Send right now (the server applies the same `validateInvoice`). */
export function localIssues(
  form: InvoiceForm,
  seller: SellerProfile,
  customer: Customer | null,
  totals: InvoiceTotals,
): InvoiceIssue[] {
  return validateInvoice({
    documentTitle: seller.gst_registered ? "tax_invoice" : "invoice",
    totalMinor: totals.totalMinor,
    sellerName: seller.business_name,
    sellerAbn: seller.abn,
    buyerName: customer?.name ?? orNull(form.buyerName),
    buyerAbn: customer?.abn ?? orNull(form.buyerAbn),
    lineCount: form.lines.length,
  })
}

/**
 * Applies a "Say it" proposal to a fresh form. Only what the proposal states is set; the rest
 * keeps the form's defaults. Totals and GST stay with the editor (rules package), so nothing the
 * model said is ever used as a number beyond the parsed line amounts. A customer that did not
 * match uniquely leaves the field empty: the user picks one.
 */
export function applyDraftProposal(
  form: InvoiceForm,
  proposal: InvoiceDraftResponse,
  customers: readonly Customer[],
  defaultTermsDays: number,
): InvoiceForm {
  const customer = customers.find((entry) => entry.id === proposal.customer_id) ?? null
  let next = customer ? applyCustomer(form, customer, defaultTermsDays) : form
  if (proposal.due_days !== null) {
    next = { ...next, dueDate: addDays(next.issueDate, proposal.due_days) }
  }
  const lines = proposal.lines.map((line, index) =>
    newLine(
      {
        description: line.description,
        quantity: formatQuantityInput(line.quantity_milli),
        unitAmount: formatAmountInput(line.unit_amount_minor),
        taxCode: line.tax_code === "GST_FREE" ? "GST_FREE" : "GST",
      },
      index,
    ),
  )
  return {
    ...next,
    amountsAre:
      proposal.amounts_are === "inclusive" || proposal.amounts_are === "exclusive"
        ? proposal.amounts_are
        : next.amountsAre,
    lines: lines.length > 0 ? lines : next.lines,
    note: proposal.reference ?? next.note,
  }
}
